import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ticketApi } from './api';
import type { TicketCategory, TicketPriority } from './api';
import { TICKET_CONTEXT_KEY } from '../../utils/assetFailureEscalation';
import type { AssetFailureTicketContext } from '../../utils/assetFailureEscalation';
import {
  Ticket, AlertTriangle, CheckCircle2, X, Send, ArrowRight,
  Mail, Tag, FileText, Zap, Package, Hash, Headphones, Flag, Clock, ShieldCheck
} from 'lucide-react';

const PRIORITIES: TicketPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

const PRIORITY_STYLES: Record<TicketPriority, string> = {
  LOW: 'bg-gradient-to-r from-slate-100 to-gray-100 text-gray-700 ring-1 ring-gray-300',
  MEDIUM: 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 ring-1 ring-blue-300',
  HIGH: 'bg-gradient-to-r from-amber-100 to-orange-100 text-orange-700 ring-1 ring-orange-300',
  CRITICAL: 'bg-gradient-to-r from-rose-100 to-red-100 text-red-700 ring-1 ring-red-300',
};

const PRIORITY_HELP_TEXT: Record<TicketPriority, string> = {
  LOW: 'Low priority for minor issues',
  MEDIUM: 'Medium priority for standard issues',
  HIGH: 'High priority for urgent issues',
  CRITICAL: 'Critical priority for system outages',
};

const emptyForm = {
  categoryId: '',
  subject: '',
  description: '',
  raisedBy: '',
  priority: 'MEDIUM' as TicketPriority,
  assetId: '',
  productOrCampaign: '',
};

function getLoggedInEmail() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return user?.email || '';
  } catch {
    return '';
  }
}

export default function RaiseTicket() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<TicketCategory[]>([]);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [priorityOpen, setPriorityOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm, raisedBy: getLoggedInEmail() });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ id: string; reference: string; resolutionDueAt: string } | null>(null);
  const [assetFailure, setAssetFailure] = useState<AssetFailureTicketContext | null>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(TICKET_CONTEXT_KEY) || 'null');
    } catch {
      return null;
    }
  });

  useEffect(() => {
    ticketApi.getCategories().then((loadedCategories) => {
      setCategories(loadedCategories);
      if (assetFailure) {
        const validAssetId = assetFailure.assetId &&
          /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(assetFailure.assetId)
          ? assetFailure.assetId
          : '';
        const ingestionCategory = loadedCategories.find(category => category.code === 'INGESTION_FAILURE');
        setForm(current => ({
          ...current,
          categoryId: ingestionCategory ? String(ingestionCategory.id) : current.categoryId,
          subject: `Asset upload failed after 3 attempts: ${assetFailure.fileName}`,
          description: `${assetFailure.source}\n\nAsset: ${assetFailure.fileName}\n${assetFailure.trackingId ? `Upload tracking ID: ${assetFailure.trackingId}\n` : ''}${validAssetId ? `Asset UUID: ${validAssetId}\n` : 'Asset UUID: Not created because ingestion did not complete\n'}Failure: ${assetFailure.error}\nAttempts: ${assetFailure.attempts}`,
          priority: 'HIGH',
          assetId: validAssetId
        }));
      }
    }).catch((err: Error) => {
      // Category load failures (e.g. demo mode) shouldn't block the form with a banner
      console.warn("Unable to load ticket categories:", err.message);
    });
  }, []);

  const selectedCategory = categories.find((category) => String(category.id) === String(form.categoryId));

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const ticket = await ticketApi.raiseTicket({ ...form, categoryId: Number(form.categoryId) });
      setCreated(ticket);
      sessionStorage.removeItem(TICKET_CONTEXT_KEY);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to raise ticket';
      if (!message.toLowerCase().includes("isn't available in the demo")) setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  /* ──────────────── SUCCESS STATE ──────────────── */
  if (created) {
    return (
      <div className="w-full flex flex-col items-center">
        <div className="max-w-2xl w-full mx-auto mt-2">
          <div className="bg-white rounded-2xl shadow-sm border border-blue-100 overflow-hidden">
            <div className="px-6 py-5 border-b flex items-center gap-4 border-blue-50 bg-gradient-to-r from-blue-50/50 to-white">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white shadow-sm bg-gradient-to-br from-blue-500 to-indigo-600">
                <CheckCircle2 size={22} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Ticket raised successfully</h2>
                <p className="text-sm text-gray-500">
                  Your support request has been logged.
                </p>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="p-4 rounded-xl border border-blue-100 bg-blue-50/50">
                <div className="text-xs font-semibold uppercase tracking-wider mb-1 text-blue-600">
                  Reference Number
                </div>
                <div className="text-xl font-bold text-gray-900 font-mono">
                  {created.reference}
                </div>
              </div>

              <p className="text-sm text-gray-600 leading-relaxed">
                We aim to respond within <strong className="text-gray-900">48 hours</strong> and resolve within{' '}
                <strong className="text-gray-900">5 days</strong>. Resolution is targeted by{' '}
                <strong className="text-gray-900">
                  {new Date(created.resolutionDueAt).toLocaleString()}
                </strong>.
              </p>

              <div className="flex flex-wrap gap-3 pt-2">
                <button
                  onClick={() => navigate(`/tickets/view/${created.id}`)}
                  className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm hover:brightness-110 active:scale-[0.98] transition-all bg-gradient-to-r from-blue-600 to-indigo-600"
                >
                  <span>View ticket</span>
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                </button>
                <button
                  onClick={() => {
                    setCreated(null);
                    setForm({ ...emptyForm, raisedBy: getLoggedInEmail() || form.raisedBy });
                  }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 active:scale-[0.98] transition-all duration-200"
                >
                  Raise another
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ──────────────── FORM STATE ──────────────── */
  return (
    <div className="w-full flex flex-col items-center">
      <div className="w-full">

        {/* Main Form Card */}
        <div className="bg-white rounded-2xl shadow-[0_2px_10px_-3px_rgba(6,81,237,0.1)] border border-blue-100 overflow-hidden relative">

          {/* Decorative background accent for header */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />


          {/* Header */}
          <div className="relative isolate flex items-center gap-3 overflow-hidden border-b border-blue-100 bg-gradient-to-r from-blue-50 via-blue-50 to-indigo-100 px-4 py-2.5 sm:px-5">
            {/* Decorative background accent */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -right-5 -top-10 h-28 w-28 rounded-full bg-blue-200/40"
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -right-12 -bottom-16 h-32 w-32 rounded-full bg-indigo-200/30"
            />

            {/* Support icon */}
            <div className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-sm">
              <Headphones size={19} strokeWidth={2} />
            </div>

            {/* Title and subtitle */}
            <div className="relative z-10 min-w-0">
              <h2 className="text-lg font-bold leading-5 text-slate-900">
                Raise a support ticket
              </h2>
            </div>
          </div>

          <form onSubmit={onSubmit} className="p-4 sm:p-5 space-y-3">
            {/* Asset failure alert */}
            {assetFailure && (
              <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 shrink-0 mt-0.5">
                  <AlertTriangle size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-amber-900">
                    Asset upload failed three times
                  </div>
                  <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                    Review the prefilled details below and raise a support ticket.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    sessionStorage.removeItem(TICKET_CONTEXT_KEY);
                    setAssetFailure(null);
                  }}
                  className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-amber-600 hover:bg-amber-100 transition"
                  aria-label="Dismiss"
                >
                  <X size={14} />
                </button>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="flex items-start gap-3 p-4 rounded-xl border border-red-200 bg-red-50">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center text-red-600 shrink-0 mt-0.5">
                  <AlertTriangle size={16} />
                </div>
                <p className="text-sm text-red-700 leading-relaxed">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Category */}
            <div>
              <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-1.5">
                <Tag size={16} className="text-gray-500" />
                Category <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <button
                  type="button"
                  aria-haspopup="listbox"
                  aria-expanded={categoryOpen}
                  onClick={() => setCategoryOpen((open) => !open)}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      setCategoryOpen(false);
                    }
                  }}

                  className={`!flex !w-full !items-center !justify-between !rounded-xl !border !px-4 !py-2.5 !text-left !text-sm !font-normal !normal-case !shadow-none !transition-all !duration-200 focus:!outline-none ${categoryOpen
                    ? "!text-gray-800"
                    : "!text-gray-800"
                    }`}
                  style={{
                    border: categoryOpen
                      ? "1px solid #2563eb"
                      : "1px solid #cbd5e1",
                    backgroundColor: "#ffffff",
                    boxShadow: categoryOpen
                      ? "0 0 0 3px rgba(37, 99, 235, 0.12)"
                      : "none",
                  }}
                >
                  <span className={selectedCategory ? "text-gray-800" : "text-gray-400"}>
                    {selectedCategory?.name || "Select a category..."}
                  </span>

                  <svg
                    className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${categoryOpen ? "rotate-180" : ""
                      }`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    aria-hidden="true"
                  >
                    <path
                      d="m6 9 6 6 6-6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {categoryOpen && (
                  <div
                    role="listbox"
                    aria-label="Ticket category"
                    className="absolute left-0 right-0 top-full z-50 mt-1.5 max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-white p-1 shadow-lg shadow-gray-900/5"
                  >
                    {categories.map((category) => {
                      const isSelected =
                        String(category.id) === String(form.categoryId);

                      return (
                        <button
                          key={category.id}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          onClick={() => {
                            setForm((current) => ({
                              ...current,
                              categoryId: String(category.id),
                            }));
                            setCategoryOpen(false);
                          }}
                          className={`!flex !w-full !items-center !justify-between !rounded-md !px-3 !py-2.5 !text-left !text-sm !normal-case !shadow-none !transition-colors ${isSelected
                            ? "!bg-gray-100 !font-semibold !text-gray-900"
                            : "!bg-white !font-normal !text-gray-700 hover:!bg-gray-50 hover:!text-gray-900"
                            }`}
                        >
                          <span>{category.name}</span>

                          {isSelected && (
                            <span className="text-xs text-gray-500">Selected</span>
                          )}
                        </button>
                      );
                    })}

                    {categories.length === 0 && (
                      <p className="px-3 py-4 text-center text-sm text-gray-500">
                        No categories available.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {selectedCategory && (
                <p className="text-xs text-gray-500 mt-1.5 pl-1 leading-relaxed">
                  {selectedCategory.description}
                </p>
              )}
            </div>

            {/* Subject */}
            <div>
              <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-1.5">
                <FileText size={16} className="text-gray-500" />
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                value={form.subject}
                onChange={(event) => setForm({ ...form, subject: event.target.value })}
                maxLength={200}
                required
                placeholder="Short summary of the issue"
                className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
              />
            </div>
            </div>

            {/* Description */}
            <div>
              <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-1.5">
                <FileText size={16} className="text-gray-500" />
                Description <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <textarea
                  value={form.description}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                  rows={4}
                  required
                  placeholder="What happened, affected assets, steps to reproduce..."
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition resize-none pb-8"
                />
                <div className="absolute bottom-3 right-4 text-xs text-gray-400 pointer-events-none">
                  {form.description.length}/1000
                </div>
              </div>
            </div>

            {/* Email + Priority */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-1.5">
                  <Mail size={16} className="text-gray-500" />
                  Your email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={form.raisedBy}
                  onChange={(event) => setForm({ ...form, raisedBy: event.target.value })}
                  required
                  placeholder="you@company.com"
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
                />
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-1.5">
                  <Flag size={16} className="text-gray-500" />
                  Priority
                </label>

                <div className="relative">
                  <button
                    type="button"
                    aria-haspopup="listbox"
                    aria-expanded={priorityOpen}
                    onClick={() => setPriorityOpen((open) => !open)}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") setPriorityOpen(false);
                    }}
                    className="!flex !w-full !items-center !justify-between !rounded-xl !border !px-4 !py-2.5 !text-left !text-sm !font-normal !normal-case !shadow-none !transition-all"
                    style={{
                      border: priorityOpen
                        ? "1px solid #2563eb"
                        : "1px solid #cbd5e1",
                      backgroundColor: "#ffffff",
                      color: "#111827",
                      boxShadow: priorityOpen
                        ? "0 0 0 3px rgba(37, 99, 235, 0.12)"
                        : "none",
                    }}
                  >
                    <span className="flex items-center gap-3">
                      <span
                        className={`h-2.5 w-2.5 rounded-full ${form.priority === "LOW"
                          ? "bg-gray-400"
                          : form.priority === "MEDIUM"
                            ? "bg-amber-400"
                            : form.priority === "HIGH"
                              ? "bg-orange-500"
                              : "bg-red-500"
                          }`}
                      />
                      <span>{form.priority}</span>
                    </span>

                    <svg
                      className={`h-4 w-4 text-gray-500 transition-transform ${priorityOpen ? "rotate-180" : ""
                        }`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      aria-hidden="true"
                    >
                      <path
                        d="m6 9 6 6 6-6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {priorityOpen && (
                    <div
                      role="listbox"
                      aria-label="Select priority"
                      className="absolute left-0 right-0 top-full z-50 mt-1.5 rounded-xl border border-gray-200 bg-white p-1 shadow-lg shadow-gray-900/10"
                    >
                      {PRIORITIES.map((priority) => {
                        const isSelected = form.priority === priority;

                        return (
                          <button
                            key={priority}
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            onClick={() => {
                              setForm((current) => ({
                                ...current,
                                priority,
                              }));
                              setPriorityOpen(false);
                            }}
                            className={`!flex !w-full !items-center !justify-between !rounded-lg !px-3 !py-2.5 !text-left !text-sm !normal-case !shadow-none !transition-colors ${isSelected
                              ? "!bg-gray-100 !font-semibold !text-gray-900"
                              : "!bg-white !font-normal !text-gray-700 hover:!bg-gray-50 hover:!text-gray-900"
                              }`}
                          >
                            <span className="flex items-center gap-3">
                              <span
                                className={`h-2.5 w-2.5 rounded-full ${priority === "LOW"
                                  ? "bg-gray-400"
                                  : priority === "MEDIUM"
                                    ? "bg-amber-400"
                                    : priority === "HIGH"
                                      ? "bg-orange-500"
                                      : "bg-red-500"
                                  }`}
                              />
                              {priority}
                            </span>

                            {isSelected && (
                              <span className="text-xs text-gray-500">Selected</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-1.5 text-xs text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100/50 w-fit">
                  <span className="text-blue-500 font-bold">i</span>
                  <span>{PRIORITY_HELP_TEXT[form.priority]}</span>
                </div>
              </div>
            {/* Asset ID + Product/Campaign (same grid as Email + Priority) */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-1.5">
                  <Hash size={16} className="text-gray-500" />
                  Asset ID
                </label>
                <input
                  value={form.assetId}
                  onChange={(event) => setForm({ ...form, assetId: event.target.value })}
                  placeholder="e.g. AST-44021"
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
                />
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-1.5">
                  <Package size={16} className="text-gray-500" />
                  Product / Campaign
                </label>
                <input
                  value={form.productOrCampaign}
                  onChange={(event) => setForm({ ...form, productOrCampaign: event.target.value })}
                  placeholder="for allocation requests"
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
                />
              </div>
            </div>

            {/* Submit */}
            <div className="pt-3 border-t border-gray-100">
              <button
                type="submit"
                disabled={submitting}
                className={`group inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm transition-all duration-200 active:scale-[0.98] ${submitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:brightness-110 hover:shadow-md"
                  }`}
              >
                <Send size={16} className="transition-transform group-hover:translate-x-0.5" />
                <span>{submitting ? 'Submitting...' : 'Submit Ticket'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}